/**
 * API Service Configuration
 * 
 * Central Axios configuration and interceptors for the Appraisal 360 application.
 * This service handles authentication, request/response interceptors, error handling,
 * and provides a consistent API interface throughout the application.
 */

import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, AxiosProgressEvent } from 'axios';
import { config, isDevelopment } from '../config/environment';
import { emitGlobalError } from '../utils/globalErrorHandler';

// Base API configuration from environment
const API_BASE_URL = config.apiBaseUrl;
const REQUEST_TIMEOUT = config.apiTimeout;

/**
 * Custom error types for better error handling
 */
export interface ApiError<T = Record<string, unknown>> {
  message?: string;
  status?: boolean;
  status_code: string | number;
  error?: string | null;
  data?: T | null; 
}

/**
 * API Response wrapper interface
 */
export interface ApiResponse<T = Record<string, unknown>> {
  message?: string;
  status?: boolean;
  status_code: string | number;
  error?: string | null;
  data?: T | null;
}

/**
 * Request data type for API calls - more flexible to accept any object structure
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestData = any;

/**
 * Main Axios instance with base configuration
 */
class ApiService {
  private api: AxiosInstance;
  private externalApi: AxiosInstance;

  constructor() {
    const axiosConfig = {
      baseURL: API_BASE_URL,
      timeout: REQUEST_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    };

    this.api = axios.create(axiosConfig);
    this.externalApi = axios.create({
      ...axiosConfig,
      withCredentials: true,
      withXSRFToken: true,
      xsrfCookieName: 'csrftoken',
      xsrfHeaderName: 'X-CSRFToken',
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - Add auth token to requests
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token && config.headers) {
          config.headers.Authorization = `Token ${token}`;
        }
        // Add request timestamp for debugging
        (config as unknown as { metadata?: { startTime: Date } }).metadata = { startTime: new Date() };
        return config;
      },
      (error) => {
        return Promise.reject(this.handleError(error));
      }
    );

    // Response interceptor - Handle responses and errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log request duration in development
        const metadata = (response.config as unknown as { metadata?: { startTime: Date } }).metadata;
        if (isDevelopment && metadata) {
          const duration = new Date().getTime() - metadata.startTime.getTime();
          console.log(`API Request: ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
        }

        return response;
      },
      (error: AxiosError) => {
        return Promise.reject(this.handleError(error));
      }
    );

    this.externalApi.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => Promise.reject(this.handleExternalError(error)),
    );
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Handle and format API errors
   */
  private handleError(error: AxiosError): ApiError {
    const apiError: ApiError = {
      message: 'An unexpected error occurred',
      status_code: 500,
    };

    if (error.response) {
      // Server responded with error status
      apiError.status_code = error.response.status;

      const data = error.response.data as unknown;
      apiError.data = (typeof data === 'object' && data !== null) ? (data as Record<string, unknown>) : { data };

      // Prefer message from error.response.data["message"]
      let extractedMessage: string | undefined;

      if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        if ('message' in obj && obj.message !== undefined && obj.message !== null) {
          const m = obj.message;
          if (typeof m === 'string') {
            extractedMessage = m;
          } else if (Array.isArray(m)) {
            extractedMessage = m.join(', ');
          } else {
            // object or other type
            try {
              extractedMessage = JSON.stringify(m);
            } catch {
              extractedMessage = String(m);
            }
          }
        } else if ('error' in obj && typeof obj.error === 'string') {
          extractedMessage = obj.error;
        } else if ('detail' in obj && typeof obj.detail === 'string') {
          extractedMessage = obj.detail;
        }
      } else if (typeof data === 'string') {
        extractedMessage = data;
      }

      apiError.message = extractedMessage || error.message || 'Server error occurred';

      // Handle specific status codes
      switch (error.response.status) {
        case 401:
          apiError.message = apiError.message || 'Authentication required. Please log in again.';
          this.handleUnauthorized();
          break;
        case 403:
          apiError.message = apiError.message || 'You do not have permission to access this resource.';
          break;
        case 404:
          apiError.message = apiError.message || 'The requested resource was not found.';
          break;
        case 422:
          apiError.message = apiError.message || 'Validation error. Please check your input.';
          break;
        case 500:
          apiError.message = apiError.message || 'Internal server error. Please try again later.';
          this.dispatchCriticalError(500, apiError, error);
          break;
        case 502:
          apiError.message = apiError.message || 'Bad gateway. Please try again later.';
          // Don't auto-redirect for 502 errors - let the component handle it
          this.dispatchCriticalError(502, apiError, error);
          break;
        case 504:
          apiError.message = apiError.message || 'Gateway timeout. The server did not respond in time.';
          // Don't auto-redirect for 504 errors - let the component handle it
          this.dispatchCriticalError(504, apiError, error);
          break;
        
      }
    } else if (error.request) {
      // Network error or no response
      apiError.message = 'Bad gateway. Please try again later.';
      apiError.status_code = 0;
      // Don't auto-redirect for network errors - let the component handle it
    } else {
      // Request setup error
      apiError.message = error.message || 'Request configuration error';
    }

    return apiError;
  }

  private handleExternalError(error: AxiosError): ApiError {
    const apiError: ApiError = {
      message: 'An unexpected error occurred',
      status_code: 500,
    };

    if (error.response) {
      apiError.status_code = error.response.status;

      const data = error.response.data as unknown;
      apiError.data = (typeof data === 'object' && data !== null) ? (data as Record<string, unknown>) : { data };

      const getMessage = (value: unknown): string | undefined => {
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.join(', ');
        if (value !== null && typeof value === 'object') {
          try {
            return JSON.stringify(value);
          } catch {
            return String(value);
          }
        }
        return undefined;
      };

      if (data && typeof data === 'object') {
        const body = data as Record<string, unknown>;
        apiError.message = getMessage(body.message)
          || getMessage(body.error)
          || getMessage(body.detail)
          || error.message
          || 'Server error occurred';
      } else {
        apiError.message = getMessage(data) || error.message || 'Server error occurred';
      }
    } else if (error.request) {
      apiError.message = 'Bad gateway. Please try again later.';
      apiError.status_code = 0;
    } else {
      apiError.message = error.message || 'Request configuration error';
    }

    return apiError;
  }

  private dispatchCriticalError(status: number, apiError: ApiError, error: AxiosError): void {
    const errorType = this.mapStatusToErrorType(status);
    if (!errorType) {
      return;
    }

    const previousUrl = window.location.pathname + window.location.search + window.location.hash;
    const retryUrl = previousUrl;

    const retryAction = error.config
      ? async () => {
          await this.api.request(error.config as AxiosRequestConfig);
        }
      : undefined;

    emitGlobalError({
      statusCode: status,
      message: apiError.message,
      errorType,
      retryAction,
      retryUrl,
      previousUrl,
      requestConfig: error.config,
    });
  }

  private mapStatusToErrorType(
    status: number
  ): 'network' | 'server' | 'timeout' | 'bad-gateway' | undefined {
    switch (status) {
      case 500:
        return 'server';
      case 502:
        return 'bad-gateway';
      case 504:
        return 'timeout';
      default:
        return undefined;
    }
  }

  /**
   * Handle unauthorized access - clear token and redirect to login
   */
  private handleUnauthorized(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('auth_user');
    // Dispatch logout action or redirect to login page
    window.location.href = '/login';
  }

  /**
   * Generic GET request
   */
  async get<T = Record<string, unknown>>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  /**
   * Generic POST request
   */
  async post<T = Record<string, unknown>>(url: string, data?: RequestData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  /**
   * Generic PUT request
   */
  async put<T = Record<string, unknown>>(url: string, data?: RequestData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.put<ApiResponse<T>>(url, data, config);
    
    return response.data;
  }

  /**
   * Generic PATCH request
   */
  async patch<T = Record<string, unknown>>(url: string, data?: RequestData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  /**
   * Generic DELETE request
   */
  async delete<T = Record<string, unknown>>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  async externalGet<T = Record<string, unknown>>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.externalApi.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async externalPost<T = Record<string, unknown>>(url: string, data?: RequestData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.externalApi.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async externalPut<T = Record<string, unknown>>(url: string, data?: RequestData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.externalApi.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  /**
   * Upload file with progress tracking
   */
  async uploadFile<T = Record<string, unknown>>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.api.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });

    return response.data;
  }

  /**
   * Set authentication token
   */
  setAuthToken(token: string): void {
    localStorage.setItem('authToken', token);
  }

  /**
   * Clear authentication token
   */
  clearAuthToken(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('auth_user');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  /**
   * Get raw Axios instance for advanced usage
   */
  getAxiosInstance(): AxiosInstance {
    return this.api;
  }

  /**
   * TEST ONLY: Simulate a 504 Gateway Timeout error
   * This is useful for testing error states in development
   * @throws {ApiError} A simulated 504 error
   */
  async simulateGatewayTimeout(): Promise<never> {
    console.warn('🧪 TEST: Simulating 504 Gateway Timeout error');
    
    const previousUrl = window.location.pathname + window.location.search + window.location.hash;
    
    // Directly emit the global error to trigger navigation
    emitGlobalError({
      statusCode: 504,
      message: 'Gateway timeout. The server did not respond in time.',
      errorType: 'timeout',
      retryUrl: previousUrl,
      previousUrl: previousUrl,
      requestConfig: {
        method: 'GET',
        url: '/test/simulate-timeout',
        baseURL: API_BASE_URL,
      },
    });

    // Also throw the error for consistency
    const mockError = {
      config: {
        method: 'GET',
        url: '/test/simulate-timeout',
        baseURL: API_BASE_URL,
      },
      response: {
        status: 504,
        statusText: 'Gateway Timeout',
        data: {
          message: 'Gateway timeout. The server did not respond in time.',
        },
        headers: {},
        config: {} as AxiosRequestConfig,
      },
      isAxiosError: true,
      toJSON: () => ({}),
      name: 'AxiosError',
      message: 'Gateway timeout. The server did not respond in time.',
    } as AxiosError;

    throw mockError;
  }

  /**
   * TEST ONLY: Simulate a 502 Bad Gateway error
   * @throws {ApiError} A simulated 502 error
   */
  async simulateBadGateway(): Promise<never> {
    console.warn('🧪 TEST: Simulating 502 Bad Gateway error');
    
    const previousUrl = window.location.pathname + window.location.search + window.location.hash;
    
    emitGlobalError({
      statusCode: 502,
      message: 'Bad gateway. The server encountered a temporary error.',
      errorType: 'bad-gateway',
      retryUrl: previousUrl,
      previousUrl: previousUrl,
      requestConfig: {
        method: 'GET',
        url: '/test/simulate-bad-gateway',
        baseURL: API_BASE_URL,
      },
    });

    const mockError = {
      config: {
        method: 'GET',
        url: '/test/simulate-bad-gateway',
        baseURL: API_BASE_URL,
      },
      response: {
        status: 502,
        statusText: 'Bad Gateway',
        data: {
          message: 'Bad gateway. The server encountered a temporary error.',
        },
        headers: {},
        config: {} as AxiosRequestConfig,
      },
      isAxiosError: true,
      toJSON: () => ({}),
      name: 'AxiosError',
      message: 'Bad gateway. The server encountered a temporary error.',
    } as AxiosError;

    throw mockError;
  }

  /**
   * TEST ONLY: Simulate a 500 Internal Server Error
   * @throws {ApiError} A simulated 500 error
   */
  async simulateServerError(): Promise<never> {
    console.warn('🧪 TEST: Simulating 500 Internal Server Error');
    
    const previousUrl = window.location.pathname + window.location.search + window.location.hash;
    
    emitGlobalError({
      statusCode: 500,
      message: 'Internal server error. Please try again later.',
      errorType: 'server',
      retryUrl: previousUrl,
      previousUrl: previousUrl,
      requestConfig: {
        method: 'GET',
        url: '/test/simulate-server-error',
        baseURL: API_BASE_URL,
      },
    });

    const mockError = {
      config: {
        method: 'GET',
        url: '/test/simulate-server-error',
        baseURL: API_BASE_URL,
      },
      response: {
        status: 500,
        statusText: 'Internal Server Error',
        data: {
          message: 'Internal server error. Please try again later.',
        },
        headers: {},
        config: {} as AxiosRequestConfig,
      },
      isAxiosError: true,
      toJSON: () => ({}),
      name: 'AxiosError',
      message: 'Internal server error. Please try again later.',
    } as AxiosError;

    throw mockError;
  }

  /**
   * TEST ONLY: Simulate a network error (no response)
   * @throws {ApiError} A simulated network error
   */
  async simulateNetworkError(): Promise<never> {
    console.warn('🧪 TEST: Simulating network error');
    
    const previousUrl = window.location.pathname + window.location.search + window.location.hash;
    
    emitGlobalError({
      statusCode: 0,
      message: 'Network error. Unable to connect to the server.',
      errorType: 'network',
      retryUrl: previousUrl,
      previousUrl: previousUrl,
      requestConfig: {
        method: 'GET',
        url: '/test/simulate-network-error',
        baseURL: API_BASE_URL,
      },
    });
    
    // Network error has no response
    const mockError = {
      config: {
        method: 'GET',
        url: '/test/simulate-network-error',
        baseURL: API_BASE_URL,
      },
      request: {}, // Has request but no response
      isAxiosError: true,
      toJSON: () => ({}),
      name: 'AxiosError',
      message: 'Network Error',
    } as AxiosError;

    throw mockError;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
